import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { API_CONFIG } from '@/config/api'

class ApiService {
  private api: AxiosInstance

  constructor() {
    this.api = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
      headers: {
        'Content-Type': 'application/json'
      }
    })

    this.setupInterceptors()
  }

  private setupInterceptors() {
    // 请求拦截器
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // 响应拦截器
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        return response.data
      },
      (error) => {
        // 登录/注册接口返回 401（密码错误等）时不强制跳转登录页，让调用方展示错误提示
        const url = error.config?.url || '';
        const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register');
        if (error.response?.status === 401 && !isAuthEndpoint) {
          localStorage.removeItem('token')
          localStorage.removeItem('user')
          window.location.href = '/login'
        }
        return Promise.reject(error.response?.data || error.message)
      }
    )
  }

  // 通用请求方法
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.api.get(url, config)
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.api.post(url, data, config)
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.api.put(url, data, config)
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.api.delete(url, config)
  }

  // 认证相关
  async login(credentials: { username: string; password: string }) {
    return this.post(API_CONFIG.ENDPOINTS.AUTH.LOGIN, credentials)
  }

  async register(userData: {
    username: string
    email: string
    password: string
    nickname: string
  }) {
    return this.post(API_CONFIG.ENDPOINTS.AUTH.REGISTER, userData)
  }

  async adminLogin(credentials: { username: string; password: string }) {
    return this.post(API_CONFIG.ENDPOINTS.AUTH.ADMIN_LOGIN, credentials)
  }

  async getCurrentUser() {
    return this.get(API_CONFIG.ENDPOINTS.AUTH.ME)
  }

  async refreshToken() {
    return this.post(API_CONFIG.ENDPOINTS.AUTH.REFRESH)
  }

  // 帖子相关
  async getPosts(params?: {
    page?: number
    limit?: number
    category?: string
    tag?: string
    search?: string
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
  }) {
    return this.get(API_CONFIG.ENDPOINTS.POSTS.LIST, { params })
  }

  async getPostById(id: string) {
    return this.get(`${API_CONFIG.ENDPOINTS.POSTS.DETAIL}/${id}`)
  }

  async createPost(postData: FormData) {
    return this.post(API_CONFIG.ENDPOINTS.POSTS.CREATE, postData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  async updatePost(id: string, updates: any) {
    return this.put(`${API_CONFIG.ENDPOINTS.POSTS.UPDATE}/${id}`, updates)
  }

  async deletePost(id: string) {
    return this.delete(`${API_CONFIG.ENDPOINTS.POSTS.DELETE}/${id}`)
  }

  async likePost(id: string) {
    return this.post(`${API_CONFIG.ENDPOINTS.POSTS.LIKE}/${id}/like`)
  }

  async sharePost(id: string) {
    return this.post(`${API_CONFIG.ENDPOINTS.POSTS.SHARE}/${id}/share`)
  }

  async getTrendingPosts(limit?: number) {
    return this.get(API_CONFIG.ENDPOINTS.POSTS.TRENDING, {
      params: { limit }
    })
  }

  // 评论相关
  async getCommentsByPost(postId: string, params?: { page?: number; limit?: number }) {
    return this.get(`${API_CONFIG.ENDPOINTS.COMMENTS.LIST}/${postId}`, { params })
  }

  async createComment(commentData: {
    postId: string
    content: string
    parentId?: string
  }) {
    return this.post(API_CONFIG.ENDPOINTS.COMMENTS.CREATE, commentData)
  }

  async likeComment(id: string) {
    return this.post(`${API_CONFIG.ENDPOINTS.COMMENTS.LIKE}/${id}/like`)
  }

  async deleteComment(id: string) {
    return this.delete(`${API_CONFIG.ENDPOINTS.COMMENTS.DELETE}/${id}`)
  }

  async reportComment(id: string, reason: string) {
    return this.post(`${API_CONFIG.ENDPOINTS.COMMENTS.REPORT}/${id}/report`, { reason })
  }

  // 文件上传
  async uploadSingle(file: File) {
    const formData = new FormData()
    formData.append('file', file)
    return this.post(API_CONFIG.ENDPOINTS.UPLOAD.SINGLE, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  async uploadMultiple(files: File[]) {
    const formData = new FormData()
    files.forEach(file => {
      formData.append('files', file)
    })
    return this.post(API_CONFIG.ENDPOINTS.UPLOAD.MULTIPLE, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  // 管理员相关
  async getAdminStats() {
    return this.get(API_CONFIG.ENDPOINTS.ADMIN.STATS)
  }

  async getAdminUsers(params?: {
    page?: number
    limit?: number
    search?: string
    isActive?: boolean
    isBanned?: boolean
  }) {
    return this.get(API_CONFIG.ENDPOINTS.ADMIN.USERS, { params })
  }

  async updateUserStatus(id: string, status: { isActive?: boolean; isBanned?: boolean }) {
    return this.put(`${API_CONFIG.ENDPOINTS.ADMIN.USERS}/${id}/status`, status)
  }

  async getAdminPosts(params?: {
    page?: number
    limit?: number
    status?: string
    category?: string
    search?: string
  }) {
    return this.get(API_CONFIG.ENDPOINTS.ADMIN.POSTS, { params })
  }

  async reviewPost(id: string, status: 'approved' | 'rejected', reason?: string) {
    return this.put(`${API_CONFIG.ENDPOINTS.ADMIN.POSTS}/${id}/review`, { status, reason })
  }

  async deletePostAsAdmin(id: string) {
    return this.delete(`${API_CONFIG.ENDPOINTS.ADMIN.POSTS}/${id}`)
  }

  async getSystemSettings() {
    return this.get(API_CONFIG.ENDPOINTS.ADMIN.SETTINGS)
  }

  async updateSystemSettings(settings: any) {
    return this.put(API_CONFIG.ENDPOINTS.ADMIN.SETTINGS, settings)
  }
}

export const apiService = new ApiService()
export default apiService